import type { Maybe } from '@adornis/base/utilTypes.js';
import type { BuchungsheldPlugin } from './plugin-types.js';

// map plugin id to plugin
export const registeredPlugins = new Map<string, BuchungsheldPlugin>();

// map scope id to list of plugins
const activePlugins = new Map<string, BuchungsheldPlugin[]>();

/**
 * register plugin to allow activation through db linking (see registerAllPlugins)
 * @throws if plugin with same id is registered multiple times
 */
export const registerPlugin = (plugin: BuchungsheldPlugin) => {
  if (registeredPlugins.has(plugin.id))
    throw new Error('Cannot register multiple plugins with the same id: ' + plugin.id);
  registeredPlugins.set(plugin.id, plugin);
};

/**
 * activate plugin for scope, needs to be registered first
 * @throws if plugin with same id already exists in scope or if plugin was not found in registered plugins
 */
export const activatePlugin = (pluginID: string, scope: string) => {
  const plugin = registeredPlugins.get(pluginID);
  if (!plugin) throw new Error('Cannot activate unregistered plugin: ' + pluginID);

  if (!activePlugins.has(scope)) activePlugins.set(scope, []);

  // @ts-expect-error we know institution entry exists because of the line above
  const activePluginsForScope: BuchungsheldPlugin[] = activePlugins.get(scope);

  for (const registeredPlugin of activePluginsForScope) {
    if (registeredPlugin.id === plugin.id)
      throw new Error(`Cannot register multiple plugins in one scope (${scope}) with duplicate ids (${plugin.id})`);
  }

  activePluginsForScope.push(plugin);
};

/**
 * replace all active plugins of a scope, so repeated calls converge to the given list
 * unknown or duplicate plugin ids are skipped and logged
 */
export const setActivePluginsForScope = (scope: string, pluginIDs: string[]) => {
  const plugins: BuchungsheldPlugin[] = [];
  for (const pluginID of new Set(pluginIDs)) {
    const plugin = registeredPlugins.get(pluginID);
    if (!plugin) {
      console.error(`Cannot activate unregistered plugin "${pluginID}" for scope "${scope}".`);
      continue;
    }
    plugins.push(plugin);
  }

  if (plugins.length) activePlugins.set(scope, plugins);
  else activePlugins.delete(scope);
};

export const registerAndActivatePlugin =(plugin: BuchungsheldPlugin, scope: string) => {
  registerPlugin(plugin);
  activatePlugin(plugin.id, scope);
};

/**
 * @returns registered plugin for given type or, if none for the given type was registered, the default plugin for the given type
 */
export function getPluginForScope<
  Tplugin extends BuchungsheldPlugin = BuchungsheldPlugin,
  TpluginType extends Tplugin['type'] = Tplugin['type'],
  // TpluginResult extends Tplugin & { type: TpluginType } = Tplugin & { type: TpluginType },
>(pluginType: TpluginType, scope?: Maybe<string>): Extract<Tplugin, { type: TpluginType }> {
  if (!scope) return getDefaultPlugin(pluginType) as any;
  const activePluginsForInstitution = activePlugins.get(scope);
  if (!activePluginsForInstitution) {
    if (scope === 'default')
      throw new Error('No registered defaults found, did you call initDefaultPlugins before accessing any plugins?');
    return getDefaultPlugin(pluginType) as any;
  }
  const plugin = activePluginsForInstitution.find(registeredPlugin => registeredPlugin.type === pluginType);
  if (!plugin) {
    if (scope === 'default') throw new Error('No registered default plugin found for type: ' + pluginType);
    return getDefaultPlugin(pluginType) as any;
  }

  return plugin as any;
}

export function getDefaultPlugin<Tplugin extends BuchungsheldPlugin>(pluginType: Tplugin['type']) {
  return getPluginForScope(pluginType, 'default') as Tplugin;
}

export type GetPluginFactory<TUnion extends BuchungsheldPlugin> = <TType extends TUnion['type']>(
  type: TType,
  scope?: Maybe<string>,
) => Extract<TUnion, { type: TType }>;

export type GetDefaultPluginFactory<TUnion extends BuchungsheldPlugin> = <TType extends TUnion['type']>(
  type: TType,
) => Extract<TUnion, { type: TType }>;
